import {
    autoUpdate,
    flip,
    offset,
    shift,
    size,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
} from '@floating-ui/react';
import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import classNames from '../../../../utils/classNames';
import { createPlainTextDefinition } from '../elements/text/PlainText/PlainTextElement';
import { AddElementSlotInput } from './AddElementSlotInput';
import { AddElementSlotMenu } from './AddElementSlotMenu';
import { AddElementSlotPresetModal } from './AddElementSlotPresetModal';
import { useAddElementSlotActivity } from './hooks/useAddElementSlotActivity';
import { useAddElementSlotFreeTextDraft } from './hooks/useAddElementSlotFreeTextDraft';
import { useAddElementSlotKeyboardNavigation } from './hooks/useAddElementSlotKeyboardNavigation';
import { useAddElementSlotMenuIcon } from './hooks/useAddElementSlotMenuIcon';
import type { StructurePath } from '../elements/structurePath';
import type {
    EditorRenderRuntime,
    ElementDefinition,
    ElementInsertionOption,
    ElementInsertionPresetOption,
    ElementInsertionSlotKind,
    RenderContext,
    RenderContextKind,
} from '../elements/types';

interface AddElementSlotProps {
    runtime: EditorRenderRuntime;
    listPath: StructurePath;
    contextKind: RenderContextKind;
    slotKind: ElementInsertionSlotKind;
    buttonLabel: string;
    renderContext: RenderContext;
}

export function AddElementSlot({
    runtime,
    listPath,
    contextKind,
    slotKind,
    buttonLabel,
    renderContext,
}: AddElementSlotProps): JSX.Element {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [selectedPresetParentType, setSelectedPresetParentType] = useState<string | null>(null);
    const getMenuIconForElementType = useAddElementSlotMenuIcon();
    const insertionOptions = useMemo(
        () => runtime.getElementInsertionOptionsForSlot(contextKind, slotKind),
        [runtime, contextKind, slotKind]
    );

    const selectedPresetParentOption = useMemo(
        () =>
            insertionOptions.find(
                (insertionOption) => insertionOption.type === selectedPresetParentType
            ) ?? null,
        [insertionOptions, selectedPresetParentType]
    );
    const isTemplateRootSlot = slotKind === 'template-root';
    const {
        inputValue,
        draftMode,
        shouldOpenSlashMenu,
        textareaRef: draftTextareaRef,
        resetDraft,
        handleDraftValueChange,
        handleDraftKeyDown,
        handleDraftBlur,
    } = useAddElementSlotFreeTextDraft({
        enabled: !isTemplateRootSlot,
        onCommit: (draftText) => {
            runtime.editor.structureController.insertAtPath(
                listPath,
                createPlainTextDefinition(draftText)
            );
        },
        onDraftTextChange: (draftText) => {
            runtime.editor.structureController.setPendingSlotDraft(draftText);
        },
        onDraftReset: () => {
            runtime.editor.structureController.clearPendingSlotDraft();
        },
    });
    const shouldKeepActiveOnFocus = isTemplateRootSlot || draftMode !== 'idle';
    const { isSlotActive, handleFocusCapture, handleBlurCapture } = useAddElementSlotActivity(
        isMenuOpen,
        shouldKeepActiveOnFocus
    );
    const isSlashInsertionTargetVisible = slotKind === 'child-list' && isMenuOpen;

    const { refs, floatingStyles, context, placement } = useFloating({
        open: isMenuOpen,
        onOpenChange(nextIsOpen) {
            setIsMenuOpen(nextIsOpen);
            if (!nextIsOpen && draftMode === 'slash') {
                resetDraft();
            }
        },
        placement: 'top-start',
        middleware: [
            offset(8),
            flip({ fallbackAxisSideDirection: 'start', padding: 8 }),
            shift({ padding: 8, crossAxis: true }),
            size({
                padding: 8,
                apply({ availableHeight, elements }) {
                    Object.assign(elements.floating.style, {
                        maxHeight: `${availableHeight}px`,
                        overflowY: 'auto',
                    });
                },
            }),
        ],
        whileElementsMounted: autoUpdate,
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([
        useDismiss(context),
        useRole(context),
    ]);
    const templateRootReferenceProps = getReferenceProps({
        onClick: () => setIsMenuOpen((currentValue) => !currentValue),
    });

    useEffect(() => {
        if (draftMode === 'text') {
            setIsMenuOpen(false);
            return;
        }

        if (draftMode === 'slash') {
            setIsMenuOpen(shouldOpenSlashMenu);
        }
    }, [draftMode, shouldOpenSlashMenu]);

    function closePresetModal(): void {
        setSelectedPresetParentType(null);
    }

    function insertDefinition(createDefinition: () => ElementDefinition): void {
        runtime.editor.structureController.insertAtPath(listPath, createDefinition());
        resetDraft();
        setIsMenuOpen(false);
        closePresetModal();
    }

    function handleOpenMenuClick(): void {
        setIsMenuOpen(true);
        if (!draftTextareaRef.current) {
            return;
        }

        draftTextareaRef.current.focus();
    }

    function handleSelect(option: ElementInsertionOption): void {
        if (!option.isEnabled) {
            return;
        }

        if (option.hasPresetSelector) {
            resetDraft();
            setIsMenuOpen(false);
            setSelectedPresetParentType(option.type);
            return;
        }

        insertDefinition(option.createDefinition);
    }

    function handleSelectPreset(selectedPreset: ElementInsertionPresetOption): void {
        if (!selectedPresetParentOption) {
            return;
        }

        if (!selectedPresetParentOption.isEnabled || !selectedPreset.isEnabled) {
            return;
        }

        insertDefinition(selectedPreset.createDefinition);
    }

    const { activeMenuOptionIndex, handleSlotKeyDown, handleMenuOptionMouseEnter } =
        useAddElementSlotKeyboardNavigation({
            isMenuOpen,
            insertionOptions,
            onSelect: handleSelect,
            onFallbackKeyDown: handleDraftKeyDown,
        });
    const draftTextareaReferenceProps = getReferenceProps({
        onChange: (event: ChangeEvent<HTMLTextAreaElement>) => {
            handleDraftValueChange(event.currentTarget.value);
        },
        onKeyDown: handleSlotKeyDown,
        onBlur: handleDraftBlur,
        tabIndex: isSlotActive ? 0 : -1,
    });
    const floatingMenuProps = getFloatingProps();
    const showInsertHereAboveInput =
        isSlashInsertionTargetVisible && placement.startsWith('bottom');
    const showOpenMenuButton = inputValue.length === 0;

    return (
        <>
            <div
                className={classNames(
                    'w-full transition',
                    isSlotActive ? 'py-2 opacity-100' : 'py-1 opacity-90'
                )}
                onFocusCapture={handleFocusCapture}
                onBlurCapture={handleBlurCapture}
            >
                <AddElementSlotInput
                    isTemplateRootSlot={isTemplateRootSlot}
                    isSlotActive={isSlotActive}
                    showOpenMenuButton={showOpenMenuButton}
                    buttonLabel={buttonLabel}
                    inputValue={inputValue}
                    isSlashInsertionTargetVisible={isSlashInsertionTargetVisible}
                    showInsertHereAboveInput={showInsertHereAboveInput}
                    onOpenMenuClick={handleOpenMenuClick}
                    onTemplateRootNode={refs.setReference}
                    onTextareaNode={(node) => {
                        refs.setReference(node);
                        draftTextareaRef.current = node;
                    }}
                    templateRootReferenceProps={templateRootReferenceProps}
                    draftTextareaReferenceProps={draftTextareaReferenceProps}
                />
                <AddElementSlotMenu
                    isMenuOpen={isMenuOpen}
                    context={context}
                    setFloatingRef={refs.setFloating}
                    floatingStyles={floatingStyles}
                    floatingMenuProps={floatingMenuProps}
                    insertionOptions={insertionOptions}
                    activeMenuOptionIndex={activeMenuOptionIndex}
                    onSelect={handleSelect}
                    onMenuOptionMouseEnter={handleMenuOptionMouseEnter}
                    getMenuIconForElementType={getMenuIconForElementType}
                />
            </div>
            <AddElementSlotPresetModal
                runtime={runtime}
                contextKind={contextKind}
                renderContext={renderContext}
                selectedPresetParentOption={selectedPresetParentOption}
                onClose={closePresetModal}
                onSelectPreset={handleSelectPreset}
            />
        </>
    );
}
